import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, CheckSquare, Music, FolderOpen, Megaphone, Bell, LogOut, Menu, X, PoundSterling, Newspaper, Settings, Users, TrendingUp, BarChart3, Undo2, Trophy, Share2, Disc3, UserCog, FileSpreadsheet, Coins, UserMinus } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { useUndo } from '../contexts/UndoContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Permission key to route mapping
const ROUTE_PERMISSIONS = {
  '/': 'dashboard',
  '/tasks': 'tasks',
  '/live-shows': 'live_shows',
  '/files': 'files',
  '/diary': 'campaigns',
  '/social': 'social_planner',
  '/invoices': 'invoices',
  '/record-label': 'record_label',
  '/highlights': 'highlights',
  '/news': 'news',
  '/finance': 'finance',
  '/finance-overview': 'finance',
  '/settings': 'settings',
  '/artists': 'artists',
  '/artist-details': 'settings',
  '/user-management': 'user_management',
  '/royalties': 'record_label',
  '/my-earnings': 'record_label',
  '/paythroughs': 'record_label',
};

const Layout = ({ children, user, onLogout, selectedArtist, setSelectedArtist }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [artists, setArtists] = useState([]);
  const { undoItem, clearUndo } = useUndo();
  
  // Get user's effective permissions
  const permissions = user?.effective_permissions || user?.permissions || {};
  
  // Check if user has permission for a route
  const hasPermission = (href) => {
    const permKey = ROUTE_PERMISSIONS[href];
    if (!permKey) return true; // Unknown routes are allowed
    return permissions[permKey] !== false; // Default to true if not set
  };

  // Build navigation based on permissions
  const allNavItems = [
    { name: 'Dashboard', href: '/', icon: Home, permKey: 'dashboard' },
    { name: 'Tasks', href: '/tasks', icon: CheckSquare, permKey: 'tasks' },
    { name: 'Live Shows', href: '/live-shows', icon: Music, permKey: 'live_shows' },
    { name: 'Files', href: '/files', icon: FolderOpen, permKey: 'files' },
    { name: 'Diary', href: '/diary', icon: Megaphone, permKey: 'campaigns' },
    { name: 'Social', href: '/social', icon: Share2, permKey: 'social_planner' },
    { name: 'Invoices', href: '/invoices', icon: PoundSterling, permKey: 'invoices' },
    { name: 'Record Label', href: '/record-label', icon: Disc3, permKey: 'record_label' },
    { name: 'DSR Royalties', href: '/royalties', icon: FileSpreadsheet, permKey: 'record_label', managerOnly: true },
    { name: 'Paythroughs', href: '/paythroughs', icon: UserMinus, permKey: 'record_label', managerOnly: true },
    { name: 'Highlights', href: '/highlights', icon: Trophy, permKey: 'highlights' },
    { name: 'News', href: '/news', icon: Newspaper, permKey: 'news' },
    { name: 'Expenses', href: '/finance', icon: TrendingUp, permKey: 'finance', managerOnly: true },
    { name: 'Expenses Overview', href: '/finance-overview', icon: BarChart3, permKey: 'finance', managerOnly: true },
    { name: 'Management Details', href: '/settings', icon: Settings, permKey: 'settings', managerOnly: true },
    { name: 'Artists', href: '/artists', icon: Users, permKey: 'artists', managerOnly: true },
    { name: 'User Management', href: '/user-management', icon: UserCog, permKey: 'user_management' },
    // Artist-only items
    { name: 'My Expenses', href: '/finance', icon: TrendingUp, permKey: 'finance', artistOnly: true },
    { name: 'My Earnings', href: '/my-earnings', icon: Coins, permKey: 'record_label', artistOnly: true },
    { name: 'My Details', href: '/artist-details', icon: Settings, permKey: 'settings', artistOnly: true },
  ];
  
  // Filter navigation based on role and permissions
  const navigation = allNavItems.filter(item => {
    // Check role restrictions - admin has same access as manager
    const isManagerOrAdmin = user?.role === 'manager' || user?.role === 'admin';
    if (item.managerOnly && !isManagerOrAdmin) return false;
    if (item.artistOnly && user?.role !== 'artist') return false;
    
    // Check permissions
    if (item.permKey && permissions[item.permKey] === false) return false;
    
    return true;
  });

  useEffect(() => {
    fetchNotifications();
    if (user?.role === 'manager' || user?.role === 'admin') {
      fetchArtists();
    }
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const fetchArtists = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/artists`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setArtists(response.data);
    } catch (error) {
      console.error('Failed to fetch artists:', error);
    }
  };

  const markAsRead = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/notifications/${id}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to mark notification as read');
    }
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  const getSelectedArtistName = () => {
    if (!selectedArtist) return 'All Artists';
    const artist = artists.find(a => a.id === selectedArtist);
    return artist ? artist.name : 'All Artists';
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Noise Texture Overlay */}
      <div className="noise-texture fixed inset-0 z-50 pointer-events-none" />

      {/* Mobile Sidebar Backdrop - Darker for better visibility */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/70 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Scrollable and fully opaque on mobile */}
      <aside
        className={`fixed top-0 left-0 h-full w-64 bg-card border-r border-border z-50 transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full overflow-y-auto">
          {/* Logo */}
          <div className="p-6 flex-shrink-0">
            <img src="/underplay-logo.png" alt="Underplay Music" className="w-32 mb-2" />
            <h1 className="font-manrope font-extrabold text-xl text-foreground" data-testid="app-logo">
              HUG
            </h1>
            <p className="text-muted-foreground text-xs mt-1">Artist Management</p>
          </div>

          {/* Navigation - Scrollable */}
          <nav className="flex-1 space-y-2 px-6 pb-6">
            {navigation.map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl font-manrope font-medium text-sm transition-all ${
                    isActive
                      ? 'bg-primary text-primary-foreground shadow-[0_0_20px_rgba(124,58,237,0.3)]'
                      : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="w-5 h-5" strokeWidth={1.5} />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User Section - Fixed at bottom */}
          <div className="border-t border-border/50 pt-4 px-6 pb-6 flex-shrink-0 bg-card">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary font-bold">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-manrope font-semibold text-sm text-foreground truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
              </div>
            </div>
            <Button
              onClick={() => {
                setSidebarOpen(false);
                onLogout();
              }}
              variant="ghost"
              className="w-full justify-start gap-2 text-destructive hover:text-destructive hover:bg-destructive/10"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="glassmorphism border-b border-border/50 sticky top-0 z-30">
          <div className="flex items-center justify-between px-6 py-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              data-testid="mobile-menu-button"
            >
              {sidebarOpen ? <X /> : <Menu />}
            </Button>

            {/* Artist Selector for Managers - Bigger and More Prominent */}
            {(user?.role === 'manager' || user?.role === 'admin') && (
              <div className="flex-1 ml-4 max-w-md">
                <select
                  value={selectedArtist || ''}
                  onChange={(e) => setSelectedArtist(e.target.value || null)}
                  data-testid="artist-selector"
                  className="w-full px-6 py-3 bg-card border-2 border-primary/30 text-foreground rounded-xl focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none font-manrope text-lg font-semibold shadow-sm hover:border-primary/50 transition-colors"
                >
                  <option value="">🎵 All Artists</option>
                  <option value="management">🏢 Underplay Management</option>
                  {artists.map((artist) => (
                    <option key={artist.id} value={artist.id}>
                      👤 {artist.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {user?.role === 'artist' && <div className="flex-1" />}

            {/* Undo Button */}
            {undoItem && (
              <Button
                variant="outline"
                size="sm"
                className="mr-2 gap-2"
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    const endpoint = `${API}/${undoItem.type}s/restore`;
                    
                    await axios.post(
                      endpoint,
                      undoItem.data,
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    
                    toast.success(`${undoItem.type.charAt(0).toUpperCase() + undoItem.type.slice(1)} restored successfully!`);
                    clearUndo();
                    
                    // Reload the page to show the restored item
                    window.location.reload();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to undo');
                    clearUndo();
                  }
                }}
                data-testid="undo-button"
              >
                <Undo2 className="w-4 h-4" />
                Undo
              </Button>
            )}

            {/* Notifications */}
            <div className="relative">
              <Button
                variant="ghost"
                size="icon"
                className="relative"
                onClick={() => setShowNotifications(!showNotifications)}
                data-testid="notifications-button"
              >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <Badge
                    className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center p-0 bg-destructive text-destructive-foreground text-xs"
                    data-testid="notification-badge"
                  >
                    {unreadCount}
                  </Badge>
                )}
              </Button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-card/95 backdrop-blur-md border border-border/50 rounded-xl shadow-xl max-h-96 overflow-y-auto">
                  <div className="p-4 border-b border-border/50">
                    <h3 className="font-manrope font-semibold text-sm">Notifications</h3>
                  </div>
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground text-sm">
                      No notifications
                    </div>
                  ) : (
                    <div className="divide-y divide-border/50">
                      {notifications.map((notification) => (
                        <div
                          key={notification.id}
                          className={`p-4 hover:bg-accent/50 cursor-pointer transition-colors ${
                            !notification.read ? 'bg-accent/20' : ''
                          }`}
                          onClick={() => markAsRead(notification.id)}
                          data-testid={`notification-${notification.id}`}
                        >
                          <p className="text-sm text-foreground">{notification.message}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(notification.created_at).toLocaleString()}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          {(user?.role === 'manager' || user?.role === 'admin') && selectedArtist && (
            <div className="px-6 pb-3">
              <p className="text-xs text-muted-foreground">
                Viewing data for: <span className="text-primary font-medium">{getSelectedArtistName()}</span>
              </p>
            </div>
          )}
        </header>

        {/* Page Content */}
        <main className="p-6 md:p-12">{children}</main>
      </div>
    </div>
  );
};

export default Layout;